import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Sparkle, GoogleLogo } from '@phosphor-icons/react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password, remember);
      toast.success(`Welcome back, ${u.full_name}`);
      nav(u.role === 'admin' ? '/admin' : '/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden">
      <div className="hero-glow" style={{ top: -200, left: -200 }} />
      <div className="hero-glow" style={{ bottom: -200, right: -200 }} />

      <div className="w-full max-w-md relative z-10">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8" data-testid="login-logo">
          <div className="w-10 h-10 rounded-lg gradient-orange flex items-center justify-center shadow-glow-saffron">
            <Sparkle size={22} weight="fill" className="text-white" />
          </div>
          <span className="font-heading font-bold text-2xl">4xview</span>
        </Link>

        <div className="glass-strong p-8 rounded-2xl neon-border">
          <h1 className="font-heading text-3xl font-bold mb-2">Welcome back</h1>
          <p className="text-muted-foreground text-sm mb-6">Sign in to access your terminal</p>

          <Button onClick={google} data-testid="google-login-btn" variant="outline" className="w-full mb-4 h-11 bg-white/5 hover:bg-white/10 border-white/10 text-white">
            <GoogleLogo size={18} weight="bold" className="mr-2" /> Continue with Google
          </Button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-muted-foreground uppercase tracking-widest">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="data-label">Email</Label>
              <Input data-testid="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required className="mt-1 bg-surface-base border-white/10 focus:border-saffron h-11" placeholder="you@emergent.com" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="data-label">Password</Label>
                <Link to="/forgot" className="text-xs text-saffron hover:underline">Forgot?</Link>
              </div>
              <Input data-testid="login-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required className="mt-1 bg-surface-base border-white/10 focus:border-saffron h-11" placeholder="••••••••" />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="remember" checked={remember} onCheckedChange={setRemember} data-testid="login-remember" />
              <label htmlFor="remember" className="text-sm text-muted-foreground">Remember me for 30 days</label>
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full h-11 bg-saffron hover:bg-saffron-hover text-white font-semibold shadow-glow-saffron">
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>

          <p className="text-sm text-center text-muted-foreground mt-6">
            No account? <Link to="/signup" className="text-saffron hover:underline font-semibold">Create one</Link>
          </p>
        </div>
        <p className="text-center text-xs text-muted-foreground mt-6">Admin demo: admin@4xview.com / Admin@123</p>
      </div>
    </div>
  );
}
